/* ==========================================================================
   Kopstorm — Rho Nutrition protocol quiz engine
   Drives sections/kopstorm-quiz.liquid and sections/kopstorm-quiz-result.liquid
   No dependencies. Answers persist in sessionStorage under STORE_KEY.
   ========================================================================== */

(function () {
  'use strict';

  var STORE_KEY = 'kopstorm_rho_quiz';

  /* ---------------------------------------------------------------- store */

  function readStore() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function writeStore(data) {
    try {
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify(data));
    } catch (e) {
      /* private mode: quiz still works for the current page */
    }
  }

  function money(cents, format) {
    var value = (Math.round(cents) / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*amount\s*\}\}/, parts.join('.'));
  }

  function tokenize(text, answers) {
    if (!text) return '';
    return text.replace(/\[([a-z0-9_]+)\]/gi, function (match, key) {
      var value = answers[key];
      if (value === undefined || value === null || value === '') return '';
      return Array.isArray(value) ? value.join(', ') : String(value);
    });
  }

  /* ------------------------------------------------------------ cart open */

  function openCart() {
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartOpenCart();
      if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  /* ----------------------------------------------------- image slider */

  /* Splide is only bundled inside Replo sections on this theme, so it is not
     on the quiz page. Load it once, on demand, and mount the result sliders.
     If the script fails the markup still scrolls and snaps on its own. */

  var SPLIDE_SRC = 'https://cdn.jsdelivr.net/npm/@splidejs/splide@4.1.4/dist/js/splide.min.js';
  var splidePromise = null;

  function loadSplide() {
    if (window.Splide) return Promise.resolve(window.Splide);
    if (splidePromise) return splidePromise;
    splidePromise = new Promise(function (resolve, reject) {
      var tag = document.createElement('script');
      tag.src = SPLIDE_SRC;
      tag.async = true;
      tag.onload = function () {
        window.Splide ? resolve(window.Splide) : reject();
      };
      tag.onerror = reject;
      document.head.appendChild(tag);
    });
    return splidePromise;
  }

  function initShots(root) {
    var tracks = Array.prototype.slice.call(root.querySelectorAll('[data-kq-shots]'));
    tracks = tracks.filter(function (el) {
      return el.getAttribute('data-kq-shots-ready') !== 'true';
    });
    if (!tracks.length) return;

    loadSplide().then(function (Splide) {
      tracks.forEach(function (el) {
        if (el.getAttribute('data-kq-shots-ready') === 'true') return;
        el.setAttribute('data-kq-shots-ready', 'true');
        /* one slide is not a carousel */
        if (el.querySelectorAll('.splide__slide').length < 2) return;
        new Splide(el, {
          perPage: 1,
          gap: '4px',
          /* 52.5 of the 295px track, so the slide lands on the Figma 242.5 */
          padding: { right: '17.8%' },
          arrows: false,
          pagination: false,
          drag: true,
          snap: true,
          speed: 350,
          reducedMotion: { speed: 0, autoplay: 'pause' }
        }).mount();
      });
    }, function () {
      /* left as a scroll-snap row */
    });
  }

  /* ================================================================= quiz */

  function initQuiz(root) {
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-kq-step]'));
    if (!steps.length) return;

    var backBtn = root.querySelector('[data-kq-back]');
    var counter = root.querySelector('[data-kq-counter]');
    var progress = root.querySelector('[data-kq-progress]');
    var resultUrl = root.getAttribute('data-kq-next-url') || '';
    var klaviyoKey = root.getAttribute('data-kq-klaviyo-key') || '';
    var klaviyoList = root.getAttribute('data-kq-klaviyo-list') || '';
    var eventName = root.getAttribute('data-kq-event') || 'Completed Rho Quiz';
    var subscribeSource = root.getAttribute('data-kq-source') || 'Rho Quiz';

    var answers = readStore();
    var history = [];
    var current = -1;

    /* steps that count toward the visible progress bar */
    var counted = steps.filter(function (el) {
      return el.getAttribute('data-kq-counts') === 'true';
    });

    buildProgress();

    /* ------------------------------------------------------- conditions */

    function matches(step) {
      var key = step.getAttribute('data-kq-if-key');
      if (!key) return true;
      var wanted = (step.getAttribute('data-kq-if-value') || '')
        .split(',')
        .map(function (v) {
          return v.trim().toLowerCase();
        })
        .filter(Boolean);
      if (!wanted.length) return true;

      var got = answers[key];
      if (got === undefined || got === null) return false;
      var list = Array.isArray(got) ? got : [got];
      var hit = list.some(function (v) {
        return wanted.indexOf(String(v).toLowerCase()) !== -1;
      });
      return step.getAttribute('data-kq-if-not') === 'true' ? !hit : hit;
    }

    function nextIndex(from) {
      for (var i = from + 1; i < steps.length; i++) {
        if (matches(steps[i])) return i;
      }
      return -1;
    }

    /* -------------------------------------------------------- progress */

    function buildProgress() {
      if (!progress) return;
      progress.innerHTML = '';
      counted.forEach(function () {
        var seg = document.createElement('span');
        seg.className = 'kq__seg';
        progress.appendChild(seg);
      });
    }

    function paintProgress(step) {
      var position = counted.indexOf(step);
      var shown = position === -1 ? countedBefore(step) : position + 1;

      if (progress) {
        Array.prototype.forEach.call(progress.children, function (seg, i) {
          seg.classList.toggle('is-on', i < shown);
        });
      }
      if (counter) {
        counter.textContent = shown + ' / ' + counted.length;
        counter.hidden = shown === 0;
      }
    }

    function countedBefore(step) {
      var n = 0;
      for (var i = 0; i < steps.length; i++) {
        if (steps[i] === step) break;
        if (counted.indexOf(steps[i]) !== -1) n++;
      }
      return n;
    }

    /* ------------------------------------------------------------ show */

    function show(index, push) {
      if (index < 0 || index >= steps.length) return;
      if (push && current !== -1) history.push(current);

      steps.forEach(function (el) {
        el.classList.remove('is-active', 'is-entering');
      });

      var step = steps[index];
      step.classList.add('is-active', 'is-entering');
      current = index;

      root.classList.toggle('kq--bare', step.getAttribute('data-kq-bare') === 'true');

      paintProgress(step);
      if (backBtn) backBtn.hidden = history.length === 0;

      syncCta(step);
      root.dispatchEvent(
        new CustomEvent('kq:step', { bubbles: true, detail: { index: index, step: step } })
      );

      if (step.getAttribute('data-kq-type') === 'loading') runLoading(step);

      var focusable = step.querySelector('input, textarea');
      if (focusable && step.getAttribute('data-kq-autofocus') === 'true') {
        window.setTimeout(function () {
          focusable.focus();
        }, 60);
      }

      if (root.getBoundingClientRect().top < 0) {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    function advance() {
      var step = steps[current];
      var target = nextIndex(current);

      if (target === -1) {
        finish();
        return;
      }
      if (step && step.getAttribute('data-kq-submit') === 'true') subscribe();
      show(target, true);
    }

    function back() {
      var previous = history.pop();
      if (previous === undefined) return;
      current = -1;
      show(previous, false);
    }

    /* Weighted scoring. Every selected option can carry
       "bundle-key:points" pairs; the totals decide the match. Recomputed from
       the DOM at the end so back navigation and changed answers stay correct. */
    function computeScores() {
      var totals = {};
      var scored = root.querySelectorAll(
        '.kq__opt.is-on[data-kq-weights], .kq__slider-img.is-on[data-kq-weights]'
      );
      Array.prototype.forEach.call(scored, function (opt) {
        opt.getAttribute('data-kq-weights').split(',').forEach(function (pair) {
          var bits = pair.split(':');
          if (bits.length !== 2) return;
          var key = bits[0].trim().toLowerCase();
          var points = parseFloat(bits[1]);
          if (!key || isNaN(points)) return;
          totals[key] = (totals[key] || 0) + points;
        });
      });
      return totals;
    }

    function finish() {
      var scores = computeScores();
      if (Object.keys(scores).length) answers._scores = scores;
      answers._completed_at = new Date().toISOString();

      /* rank now so the matched bundle rides along to Klaviyo */
      var panel = document.querySelector('[data-kq-result]');
      if (panel && answers._scores) {
        var ranked = rankProtocols(panel, answers);
        if (ranked.best) answers._matched = ranked.best.getAttribute('data-kq-key') || '';
        if (ranked.runnerUp) answers._runner_up = ranked.runnerUp.getAttribute('data-kq-key') || '';
      }

      writeStore(answers);
      subscribe();
      trackCompleted();

      if (resultUrl) {
        window.location.href = resultUrl;
        return;
      }

      /* same-page results: hand over to the result section below the quiz */
      if (!panel) return;

      root.classList.add('kq--done');
      panel.classList.add('is-revealed');

      if (panel.getAttribute('data-kq-ready') !== 'true') {
        panel.setAttribute('data-kq-ready', 'true');
        initResult(panel);
      }

      var top = panel.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: top < 0 ? 0 : top, behavior: 'smooth' });
    }

    /* ---------------------------------------------------- answer helpers */

    function setAnswer(key, value) {
      if (!key) return;
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
        delete answers[key];
      } else {
        answers[key] = value;
      }
      writeStore(answers);
    }

    function isComplete(step) {
      var type = step.getAttribute('data-kq-type');
      var key = step.getAttribute('data-kq-key');
      var optional = step.getAttribute('data-kq-optional') === 'true';

      if (optional) return true;

      if (type === 'choice') {
        return !!step.querySelector('.kq__opt.is-on');
      }
      if (type === 'slider') {
        return true;
      }
      if (type === 'text') {
        var field = step.querySelector('[data-kq-input]');
        return !!(field && field.value.trim());
      }
      if (type === 'email') {
        /* the gate can also carry an optional first name field */
        var mail = step.querySelector('[data-kq-input="email"]');
        return !!(mail && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(mail.value.trim()));
      }
      if (type === 'name') {
        var first = step.querySelector('[data-kq-input="first_name"]');
        return !!(first && first.value.trim());
      }
      return !!key ? answers[key] !== undefined : true;
    }

    function syncCta(step) {
      var cta = step.querySelector('[data-kq-next]');
      if (!cta) return;
      var ready = isComplete(step);
      cta.disabled = !ready;
      cta.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }

    /* --------------------------------------------------------- choices */

    root.addEventListener('click', function (event) {
      var opt = event.target.closest('.kq__opt');
      if (opt && root.contains(opt)) {
        onChoice(opt);
        return;
      }
      if (event.target.closest('[data-kq-next]')) {
        event.preventDefault();
        onNext(event.target.closest('[data-kq-step]'));
        return;
      }
      if (event.target.closest('[data-kq-back]')) {
        event.preventDefault();
        back();
      }
    });

    function onChoice(opt) {
      var step = opt.closest('[data-kq-step]');
      var key = step.getAttribute('data-kq-key');
      var multi = step.getAttribute('data-kq-multi') === 'true';
      var max = parseInt(step.getAttribute('data-kq-max'), 10) || 0;
      var options = Array.prototype.slice.call(step.querySelectorAll('.kq__opt'));

      if (!multi) {
        options.forEach(function (el) {
          el.classList.toggle('is-on', el === opt);
          el.setAttribute('aria-checked', el === opt ? 'true' : 'false');
        });
        setAnswer(key, opt.getAttribute('data-kq-value'));
        setAnswer(key + '_label', (opt.querySelector('.kq__opt-label') || opt).textContent.trim());
        syncCta(step);

        if (step.getAttribute('data-kq-auto') === 'true') {
          window.setTimeout(function () {
            if (steps[current] === step) advance();
          }, 220);
        }
        return;
      }

      var exclusive = opt.getAttribute('data-kq-exclusive') === 'true';
      if (exclusive) {
        var turningOn = !opt.classList.contains('is-on');
        options.forEach(function (el) {
          el.classList.toggle('is-on', turningOn && el === opt);
        });
      } else {
        options.forEach(function (el) {
          if (el.getAttribute('data-kq-exclusive') === 'true') el.classList.remove('is-on');
        });
        opt.classList.toggle('is-on');
      }

      var chosen = options.filter(function (el) {
        return el.classList.contains('is-on');
      });

      if (max && chosen.length > max) {
        opt.classList.remove('is-on');
        chosen = chosen.filter(function (el) {
          return el !== opt;
        });
      }

      if (max) {
        var full = chosen.length >= max;
        options.forEach(function (el) {
          el.classList.toggle('is-locked', full && !el.classList.contains('is-on'));
        });
      }

      options.forEach(function (el) {
        el.setAttribute('aria-checked', el.classList.contains('is-on') ? 'true' : 'false');
      });

      setAnswer(
        key,
        chosen.map(function (el) {
          return el.getAttribute('data-kq-value');
        })
      );
      setAnswer(
        key + '_label',
        chosen.map(function (el) {
          return (el.querySelector('.kq__opt-label') || el).textContent.trim();
        })
      );
      syncCta(step);
    }

    /* ---------------------------------------------------------- sliders */

    Array.prototype.forEach.call(root.querySelectorAll('[data-kq-range]'), function (range) {
      var step = range.closest('[data-kq-step]');
      var key = step.getAttribute('data-kq-key');
      var frames = Array.prototype.slice.call(step.querySelectorAll('[data-kq-frame]'));
      var title = step.querySelector('[data-kq-frame-title]');
      var caption = step.querySelector('[data-kq-frame-caption]');

      /* read the attribute: the value property is already clamped to the
         placeholder max="0" that the markup ships with */
      var wanted = parseInt(range.getAttribute('value'), 10) || 0;
      range.max = Math.max(0, frames.length - 1);
      range.value = Math.min(wanted, Number(range.max));

      function paint() {
        var index = parseInt(range.value, 10) || 0;
        var span = Number(range.max) - Number(range.min) || 1;
        range.style.setProperty('--kq-fill', ((index - range.min) / span) * 100 + '%');

        frames.forEach(function (frame, i) {
          frame.classList.toggle('is-on', i === index);
        });
        var active = frames[index];
        if (active) {
          if (title) title.textContent = active.getAttribute('data-kq-title') || '';
          if (caption) caption.textContent = active.getAttribute('data-kq-caption') || '';
          setAnswer(key, active.getAttribute('data-kq-value'));
          setAnswer(key + '_label', active.getAttribute('data-kq-title') || '');
        }
        syncCta(step);
      }

      range.addEventListener('input', paint);
      range.addEventListener('change', paint);
      paint();
    });

    /* ----------------------------------------------------------- inputs */

    Array.prototype.forEach.call(root.querySelectorAll('[data-kq-input]'), function (field) {
      var step = field.closest('[data-kq-step]');
      var key = field.getAttribute('data-kq-input');

      if (answers[key]) field.value = answers[key];

      field.addEventListener('input', function () {
        setAnswer(key, field.value.trim());
        syncCta(step);
        var error = step.querySelector('[data-kq-error]');
        if (error) error.classList.remove('is-on');
      });

      field.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && field.tagName !== 'TEXTAREA') {
          event.preventDefault();
          onNext(step);
        }
      });
    });

    function onNext(step) {
      if (!step) return;
      if (!isComplete(step)) {
        var error = step.querySelector('[data-kq-error]');
        if (error) error.classList.add('is-on');
        return;
      }
      advance();
    }

    /* ---------------------------------------------------------- loading */

    function runLoading(step) {
      if (step.getAttribute('data-kq-ran') === 'true') return;
      step.setAttribute('data-kq-ran', 'true');

      var wait = parseInt(step.getAttribute('data-kq-wait'), 10) || 5500;
      var target = step.querySelector('[data-kq-fact]');
      var facts = [];
      try {
        facts = JSON.parse(step.getAttribute('data-kq-facts') || '[]');
      } catch (e) {
        facts = [];
      }

      var timer = null;
      if (target && facts.length > 1) {
        var index = 0;
        var every = Math.max(1600, Math.round(wait / facts.length));

        /* The line is swapped only after the fade out has finished, so the two
           facts are never on screen at the same time. The inline opacity is
           cleared once the fade in ends, which drops the compositing layer and
           gives the text its normal subpixel rendering back. */
        function swapFact(text) {
          var done = false;

          function apply() {
            if (done) return;
            done = true;
            target.removeEventListener('transitionend', apply);
            target.textContent = text;
            /* flush the paint at opacity 0 before fading back in */
            void target.offsetHeight;
            target.style.opacity = '1';
          }

          target.addEventListener('transitionend', apply);
          target.style.opacity = '0';
          window.setTimeout(apply, 420);
        }

        target.addEventListener('transitionend', function () {
          if (target.style.opacity === '1') target.style.opacity = '';
        });

        timer = window.setInterval(function () {
          index = (index + 1) % facts.length;
          swapFact(facts[index]);
        }, every);
      }

      window.setTimeout(function () {
        if (timer) window.clearInterval(timer);
        advance();
      }, wait);
    }

    /* --------------------------------------------------------- klaviyo */

    function profileAttributes() {
      var a = { email: answers.email };
      if (answers.first_name) a.first_name = answers.first_name;
      if (answers.last_name) a.last_name = answers.last_name;

      /* every stored answer rides along so a Klaviyo flow can personalise the email */
      var internal = {
        _scores: 'quiz_scores',
        _matched: 'quiz_matched_bundle',
        _runner_up: 'quiz_runner_up',
        _completed_at: 'quiz_completed_at'
      };
      var props = {};
      Object.keys(answers).forEach(function (k) {
        if (k === 'email' || k === 'first_name' || k === 'last_name') return;
        if (internal[k]) {
          props[internal[k]] = answers[k];
          return;
        }
        if (k.charAt(0) === '_') return;
        props[k] = answers[k];
      });
      a.properties = props;
      return a;
    }

    function post(url, payload) {
      return window.fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload)
      });
    }

    /* Shopify's own newsletter endpoint, used when Klaviyo is not configured
       so an address is never lost */
    function shopifyCapture() {
      if (!answers.email) return;
      var body = new URLSearchParams();
      body.append('form_type', 'customer');
      body.append('utf8', '\u2713');
      body.append('contact[email]', answers.email);
      body.append('contact[accepts_marketing]', 'true');
      body.append('contact[tags]', 'rho-quiz');
      if (answers.first_name) body.append('contact[first_name]', answers.first_name);
      if (answers.last_name) body.append('contact[last_name]', answers.last_name);

      window
        .fetch('/contact', {
          method: 'POST',
          headers: { 'content-type': 'application/x-www-form-urlencoded' },
          body: body.toString()
        })
        .catch(function () {});
    }

    /* Adds the profile to the list. Called twice on purpose: once as soon as the
       address is typed, so a drop-off is still captured, and again at the end
       with the name and the matched bundle filled in. */
    function subscribe() {
      if (!answers.email) return;

      if (!klaviyoKey || !klaviyoList) {
        shopifyCapture();
        return;
      }

      post('https://a.klaviyo.com/client/subscriptions/?company_id=' + klaviyoKey, {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: subscribeSource,
            profile: { data: { type: 'profile', attributes: profileAttributes() } }
          },
          relationships: { list: { data: { type: 'list', id: klaviyoList } } }
        }
      }).catch(function () {});
    }

    /* The metric a results-email flow triggers on. Fired ONCE, from finish(),
       so the event always carries the name and the matched bundle. Firing it at
       the email step as well would trigger the flow on a half-filled profile. */
    function trackCompleted() {
      if (!klaviyoKey || !answers.email) return;

      var profile = profileAttributes();
      post('https://a.klaviyo.com/client/events/?company_id=' + klaviyoKey, {
        data: {
          type: 'event',
          attributes: {
            properties: profile.properties,
            metric: { data: { type: 'metric', attributes: { name: eventName } } },
            profile: { data: { type: 'profile', attributes: profile } }
          }
        }
      }).catch(function () {});
    }

    /* -------------------------------------------------------- kick off */

    var first = nextIndex(-1);
    show(first === -1 ? 0 : first, false);
    if (backBtn) backBtn.hidden = true;
  }

  /* =============================================================== result */

  /* Reads the "Priority rules" field of a protocol card.
     Format: "quiz_gender:female; quiz_age:55-64,65-plus; quiz_goal:longevity".
     Semicolons are AND, commas inside one rule are OR, and the keys are the
     answer keys of the quiz questions. Returns 0 when the card carries no
     rules (normal weighted scoring), the number of rules met when all of them
     are met, and -1 when at least one is not met. */
  function ruleScore(node, answers) {
    var raw = (node.getAttribute('data-kq-rules') || '').trim();
    if (!raw) return 0;

    var clauses = raw.split(';').map(function (v) {
      return v.trim();
    }).filter(Boolean);
    if (!clauses.length) return 0;

    var met = 0;
    for (var i = 0; i < clauses.length; i++) {
      var at = clauses[i].indexOf(':');
      if (at === -1) return -1;

      var key = clauses[i].slice(0, at).trim();
      var wanted = clauses[i].slice(at + 1).split(',').map(function (v) {
        return v.trim().toLowerCase();
      }).filter(Boolean);
      if (!key || !wanted.length) return -1;

      var got = answers ? answers[key] : null;
      if (got === undefined || got === null || got === '') return -1;

      var list = Array.isArray(got) ? got : [got];
      var hit = list.some(function (v) {
        return wanted.indexOf(String(v).trim().toLowerCase()) !== -1;
      });
      if (!hit) return -1;
      met += 1;
    }
    return met;
  }

  /* Ranks the protocol cards of a result section.
     Score comes from the weights collected during the quiz. Ties are broken
     first by the goal anchor (question 1), then by the fixed order the section
     carries, exactly as the funnel spec describes. */
  function rankProtocols(root, answers) {
    var protocols = Array.prototype.slice.call(root.querySelectorAll('[data-kq-protocol]'));
    if (!protocols.length) return { best: null, runnerUp: null, ordered: [] };

    var scores = answers._scores || null;
    var goalKey = root.getAttribute('data-kq-goal-key') || 'goal';
    var goals = answers[goalKey];
    goals = (Array.isArray(goals) ? goals : goals ? [goals] : []).map(function (v) {
      return String(v).toLowerCase();
    });

    var order = (root.getAttribute('data-kq-order') || '')
      .split(',')
      .map(function (v) {
        return v.trim().toLowerCase();
      })
      .filter(Boolean);

    var ranked = protocols.map(function (node, index) {
      var rules = ruleScore(node, answers);
      var key = (node.getAttribute('data-kq-key') || '').trim().toLowerCase();
      var values = (node.getAttribute('data-kq-match') || '')
        .split(',')
        .map(function (v) {
          return v.trim().toLowerCase();
        })
        .filter(Boolean);

      var score = 0;
      if (scores && key && typeof scores[key] === 'number') {
        score = scores[key];
      } else if (!scores) {
        /* older setups without weights still match on the goal answers */
        values.forEach(function (v) {
          var at = goals.indexOf(v);
          if (at !== -1) score += goals.length - at;
        });
      }
      if (node.getAttribute('data-kq-fallback') === 'true' && score === 0) score = 0.01;

      var anchored = false;
      goals.forEach(function (g) {
        if (g && (g === key || values.indexOf(g) !== -1)) anchored = true;
      });

      var rank = order.indexOf(key);
      if (rank === -1) rank = order.length + index;

      return { node: node, score: score, anchored: anchored, rank: rank, index: index, rules: rules };
    });

    /* a protocol whose rules are not met is out of the result completely:
       not the match, not the runner up, no recommendation group */
    ranked = ranked.filter(function (item) {
      return item.rules !== -1;
    });
    if (!ranked.length) return { best: null, runnerUp: null, ordered: [] };

    ranked.sort(function (a, b) {
      if (b.rules !== a.rules) return b.rules - a.rules;
      if (b.score !== a.score) return b.score - a.score;
      if (a.anchored !== b.anchored) return a.anchored ? -1 : 1;
      return a.rank - b.rank;
    });

    var best = ranked[0];
    /* a protocol without a bundle product can only render its name, so it is
       never shown: not as the match, not as the runner up. This is what catches
       a bundle that was discontinued or put back in draft without the block
       being excluded in the editor. */
    if (best && best.node.getAttribute('data-kq-has-product') !== 'true') {
      for (var i = 1; i < ranked.length; i++) {
        if (ranked[i].node.getAttribute('data-kq-has-product') === 'true') {
          best = ranked.splice(i, 1)[0];
          ranked.unshift(best);
          break;
        }
      }
    }

    var runnerUp = null;
    for (var r = 1; r < ranked.length; r++) {
      if (ranked[r].node !== (best && best.node)
        && ranked[r].node.getAttribute('data-kq-has-product') === 'true') {
        runnerUp = ranked[r].node;
        break;
      }
    }

    return {
      best: best ? best.node : null,
      runnerUp: runnerUp,
      ordered: ranked.map(function (r) {
        return r.node;
      })
    };
  }

  function fillRunner(box, node) {
    var map = {
      '[data-kq-runner-name]': 'data-kq-name',
      '[data-kq-runner-price]': 'data-kq-price',
      '[data-kq-runner-badge]': 'data-kq-badge'
    };
    Object.keys(map).forEach(function (sel) {
      var target = box.querySelector(sel);
      if (!target) return;
      var value = node.getAttribute(map[sel]) || '';
      target.textContent = value;
      target.hidden = !value;
    });

    var image = box.querySelector('[data-kq-runner-image]');
    var src = node.getAttribute('data-kq-image');
    if (image) {
      if (src) {
        image.src = src;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }

    var link = box.querySelector('[data-kq-runner-link]');
    var url = node.getAttribute('data-kq-url');
    if (link) {
      if (url) {
        link.href = url;
        link.hidden = false;
      } else {
        link.hidden = true;
      }
    }
  }

  function initResult(root) {
    var answers = readStore();
    var format = root.getAttribute('data-kq-money') || '${{amount}}';
    var protocols = Array.prototype.slice.call(root.querySelectorAll('[data-kq-protocol]'));
    if (!protocols.length) return;

    var ranked = rankProtocols(root, answers);
    var best = ranked.best || protocols[0];

    /* runner up: second ranked bundle, low emphasis */
    var runnerBox = root.querySelector('[data-kq-runner]');
    if (runnerBox) {
      var runner = ranked.runnerUp;
      if (runner && runner.getAttribute('data-kq-has-product') === 'true') {
        fillRunner(runnerBox, runner);
        runnerBox.hidden = false;
      } else {
        runnerBox.hidden = true;
      }
    }

    protocols.forEach(function (node) {
      node.hidden = node !== best;
    });

    /* recommendations: the matched protocol's own hand picked group when it has
       one, otherwise the shared collection group (data-kq-recs="fallback") */
    var recGroups = Array.prototype.slice.call(root.querySelectorAll('[data-kq-recs]'));
    if (recGroups.length) {
      var bestId = best ? best.getAttribute('data-kq-id') || '' : '';
      var ownGroup = null;
      recGroups.forEach(function (group) {
        if (bestId && group.getAttribute('data-kq-recs') === bestId) ownGroup = group;
      });
      recGroups.forEach(function (group) {
        group.hidden = ownGroup
          ? group !== ownGroup
          : group.getAttribute('data-kq-recs') !== 'fallback';
      });

      /* the shared collection group links out per matched protocol */
      var recsCta = root.querySelector('[data-kq-recs-cta]');
      if (recsCta) {
        var recsLink = recsCta.querySelector('[data-kq-recs-link]');
        var recsUrl = best ? best.getAttribute('data-kq-rec-url') || '' : '';
        if (recsLink && recsUrl) {
          recsLink.setAttribute('href', recsUrl);
          recsCta.hidden = false;
        } else {
          recsCta.hidden = true;
        }
      }
    }

    /* the matched bundle name is only known once ranking has run, so it is
       published as a token here rather than stored during the quiz */
    if (best) answers.bundle = best.getAttribute('data-kq-name') || '';

    /* only the visible card's slider is worth mounting */
    if (best) initShots(best);

    /* token replacement across the page */
    Array.prototype.forEach.call(root.querySelectorAll('[data-kq-token]'), function (node) {
      var template = node.getAttribute('data-kq-token');
      var filled = tokenize(template, answers).replace(/\s{2,}/g, ' ').trim();
      if (filled) {
        node.innerHTML = filled;
      } else {
        var fallback = node.getAttribute('data-kq-token-empty');
        if (fallback !== null) node.innerHTML = fallback;
      }
    });

    /* subscription or one time: flip the card into the matching price state */
    root.addEventListener('change', function (event) {
      var input = event.target.closest('[data-kq-plan]');
      if (!input) return;
      var card = input.closest('[data-kq-protocol]');
      if (card) card.setAttribute('data-kq-mode', input.value ? 'sub' : 'one');
    });

    /* add to cart */
    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-kq-add]');
      if (!button) return;
      event.preventDefault();

      var variant = button.getAttribute('data-kq-variant');
      if (!variant) return;

      var label = button.textContent;
      button.disabled = true;
      button.textContent = button.getAttribute('data-kq-adding') || 'Adding...';

      var card = button.closest('[data-kq-protocol]');
      var plan = card && card.querySelector('[data-kq-plan]:checked');
      var line = {
        id: Number(variant),
        quantity: 1,
        properties: { _rho_quiz: 'protocol' }
      };
      if (plan && plan.value) line.selling_plan = Number(plan.value);

      /* free gifts ride along on the same request so the cart never flashes
         a state without them */
      var items = [line];
      (root.getAttribute('data-kq-gifts') || '').split(',').forEach(function (id) {
        var gift = parseInt(id.trim(), 10);
        if (gift) items.push({ id: gift, quantity: 1, properties: { _rho_quiz: 'gift' } });
      });

      var rewardCode = root.getAttribute('data-kq-reward-code');
      var cartUrl = root.getAttribute('data-kq-cart-url') || '/cart';

      window
        .fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'application/json' },
          body: JSON.stringify({ items: items })
        })
        .then(function (response) {
          return response.json();
        })
        .then(function () {
          button.disabled = false;
          button.textContent = label;
          /* a reward code applies itself through the discount url, so the
             shopper never has to type anything */
          if (rewardCode) {
            window.location.href =
              '/discount/' + encodeURIComponent(rewardCode) + '?redirect=' + encodeURIComponent(cartUrl);
            return;
          }
          openCart();
        })
        .catch(function () {
          button.disabled = false;
          button.textContent = label;
        });
    });

    /* prices rendered client side keep the shop's money format */
    Array.prototype.forEach.call(root.querySelectorAll('[data-kq-money-value]'), function (node) {
      node.textContent = money(parseInt(node.getAttribute('data-kq-money-value'), 10), format);
    });
  }

  /* ================================================================= boot */

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-kq-quiz]'), function (node) {
      if (node.getAttribute('data-kq-ready') === 'true') return;
      node.setAttribute('data-kq-ready', 'true');
      initQuiz(node);
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-kq-result]'), function (node) {
      if (node.getAttribute('data-kq-ready') === 'true') return;
      /* panels that wait for the quiz are initialised by finish() instead */
      if (node.getAttribute('data-kq-start-hidden') === 'true') return;
      node.setAttribute('data-kq-ready', 'true');
      initResult(node);
    });
  }

  /* the result section loads this file inline after its markup, so boot now and
     again on DOMContentLoaded; the data-kq-ready flag keeps it idempotent */
  boot();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  }

  /* theme editor re-renders */
  document.addEventListener('shopify:section:load', boot);
})();
